import { strToU8, zipSync } from "fflate";
import { api, type Attachment, type Note, type NotesPage } from "./api";
import { escapeHtml, fileName, printDocument, toMarkdown, toText, zipPaths } from "@ccnote/shared";
import { inlineAttachmentNames, renderMarkdown } from "./markdown";
import { baseName, inlineMedia, isInlineImage, isVideo, mediaUrl } from "./note-media";

export function download(name: string, data: BlobPart, type: string) {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function exportText(note: Note) {
  download(fileName(note.title, "txt"), toText(note), "text/plain;charset=utf-8");
}

export function exportMarkdown(note: Note) {
  download(fileName(note.title, "md"), toMarkdown(note), "text/markdown;charset=utf-8");
}

/**
 * PDF via the browser's print dialog ("Save as PDF"): the note is laid out as a clean page in a
 * hidden frame and printed. The frame can't run scripts; the note's title becomes the file name.
 * Pictures come along: those placed in the text where they are, then the page snapshot and any other
 * photos; videos become links.
 */
export function exportPdf(note: Note, attachments: Attachment[] = []) {
  const content = note.bodyHtml
    ? note.bodyHtml
    : note.format === "markdown"
      ? renderMarkdown(note.bodyText, inlineMedia(note.id, attachments), { forPrint: true })
      : `<div class="plain">${escapeHtml(note.bodyText)}</div>`;
  const placed = note.format === "markdown" ? inlineAttachmentNames(note.bodyText) : new Set<string>();
  const rest = attachments.filter((a) => !placed.has(baseName(a)));
  const html = printDocument(note, content, {
    pictures: rest.filter((a) => isInlineImage(a.mime)).map((a) => ({ url: mediaUrl(note.id, a), name: a.filename })),
    videos: rest.filter((a) => isVideo(a.mime)).map((a) => ({ url: mediaUrl(note.id, a), name: a.filename })),
  });

  const frame = document.createElement("iframe");
  frame.setAttribute("sandbox", "allow-same-origin allow-modals");
  frame.setAttribute("aria-hidden", "true");
  Object.assign(frame.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0" });
  frame.srcdoc = html;
  frame.onload = async () => {
    const win = frame.contentWindow!;
    // Let images finish so they appear in the PDF.
    await Promise.all([...win.document.images].map((img) => (img.complete ? null : new Promise((r) => (img.onload = img.onerror = r)))));
    win.focus();
    win.print();
    setTimeout(() => frame.remove(), 1000);
  };
  document.body.append(frame);
}

/** Every note (not Pending or Trash) as Markdown files in a zip, one folder per (first) category. */
export async function exportAll(onProgress?: (count: number) => void): Promise<number> {
  const notes: Note[] = [];
  for (const status of ["active", "archived"] as const) {
    let cursor: string | null = null;
    do {
      const params = new URLSearchParams({ status, limit: "100" });
      if (cursor) params.set("cursor", cursor);
      const page: NotesPage = await api<NotesPage>(`/v1/notes?${params}`);
      notes.push(...page.notes);
      onProgress?.(notes.length);
      cursor = page.nextCursor;
    } while (cursor);
  }
  const files: Record<string, Uint8Array> = {};
  for (const [path, note] of zipPaths(notes)) files[path] = strToU8(toMarkdown(note, { frontMatter: true }));
  files["README.txt"] = strToU8(
    `Your ccnote export, ${new Date().toISOString().slice(0, 10)}.\n\n` +
      `One Markdown file per note, in a folder per category. Each file starts with its details (title, dates, source).\n` +
      `Pending and deleted notes aren't included. Attachments aren't included: download them from each note.\n`,
  );
  download(`ccnote-export-${new Date().toISOString().slice(0, 10)}.zip`, zipSync(files, { level: 6 }), "application/zip");
  return notes.length;
}
